import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useModal } from '../../contexts/ModalContext';

interface FinalCTAProps {
  headline?: string;
  subline?: string;
  primaryCta?: string;
  secondaryCta?: string;
}

export default function FinalCTA({
  headline = 'Start Designing Your Home with Intention',
  subline = 'A conversation costs nothing. A home built without vision costs everything.',
  primaryCta = 'Schedule Consultation',
  secondaryCta = 'Call (914) 645-2940',
}: FinalCTAProps) {
  const { openModal } = useModal();
  const ref = useScrollReveal();

  return (
    <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Full-bleed background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/a877badc4957e9829f28521007e167fa.jpg)',
        }}
      />
      {/* Gradient overlay — strong from bottom */}
      <div className="absolute inset-0 bg-gradient-to-r from-dark-ink/90 via-dark-ink/70 to-dark-ink/40" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-8 lg:px-16">
        <div ref={ref} className="reveal-on-scroll max-w-2xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-8 h-px bg-bronze" />
            <span className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze">Begin Your Project</span>
          </div>

          {/* Headline */}
          <h2 className="font-serif text-5xl lg:text-7xl font-light text-white leading-[1.05] mb-6">
            {headline}
          </h2>

          {/* Subline */}
          <p className="font-sans text-base text-white/55 leading-relaxed mb-12 max-w-lg">
            {subline}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-14">
            <button
              onClick={openModal}
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-12 py-4 bg-white text-dark-ink hover:bg-soft-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              {primaryCta}
            </button>
            <a
              href="tel:+19146452940"
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-12 py-4 border border-white/30 text-white hover:bg-white/10 transition-colors duration-300 cursor-pointer whitespace-nowrap text-center"
            >
              {secondaryCta}
            </a>
          </div>

          {/* Contact bar */}
          <div className="flex flex-wrap items-center gap-8 pt-10 border-t border-white/12">
            <a href="tel:+19146452940" className="flex items-center gap-3 group cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-bronze text-sm" />
              </div>
              <span className="font-sans text-sm text-white/55 group-hover:text-white/85 transition-colors">(914) 645-2940</span>
            </a>
            <a href="mailto:talo.tapan@gmail.com" className="flex items-center gap-3 group cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-mail-line text-bronze text-sm" />
              </div>
              <span className="font-sans text-sm text-white/55 group-hover:text-white/85 transition-colors">talo.tapan@gmail.com</span>
            </a>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-2-line text-bronze text-sm" />
              </div>
              <span className="font-sans text-sm text-white/55">Scarsdale, NY 10583</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
